<template>
  <v-container grid-list-md fluid>
    <v-layout wrap justify-space-between>
      <v-flex xs12 sm2>
        <v-btn
          color="primary"
          @click="create"
          class="mb-4"
          :disabled="!hasCondominioAccess(entity, 'create')"
        >
          {{ this.translate("system.button.add.title") }}
          <v-icon dark>{{ this.translate("system.button.add.icon") }}</v-icon>
        </v-btn>
      </v-flex>
      <v-flex xs12 sm4>
        <v-text-field
          append-icon="search"
          label="Pesquisar"
          v-model="search"
          @change="updateSum"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm1>
        <v-menu offset-y>
          <v-btn slot="activator" icon class="ml-2" title="Relatórios">
            <v-icon>description</v-icon>
          </v-btn>
          <v-list>
            <!-- PDF -->
            <v-list-tile @click="exportReport(1)">
              <v-list-tile-action>
                <v-icon color="red darken-1">picture_as_pdf</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em PDF</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Excel -->
            <v-list-tile @click="exportReport(2)">
              <v-list-tile-action>
                <v-icon color="green darken-2">table_chart</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em Excel</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-flex>

      <!--Index-->
      <v-flex xs12>
        <v-data-table
          ref="RendimentoTributacaoTable"
          :headers="headers"
          :items="items"
          :search="search"
          class="elevation-1"
          :rows-per-page-items="[100, 50, 25, { text: 'Todos', value: -1 }]"
          rows-per-page-text="Registros por página"
          no-data-text="Não existem registros"
          no-results-text="Nenhum registro encontrado"
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.sequencia_ir }}</td>
            <td>{{ props.item.id_texto }}</td>
            <td>{{ getCodigoText(props.item.codigo) }}</td>
            <td>{{ props.item.cnpj | formatCpfCnpj }}</td>
            <td v-if="temDependente">
              {{ props.item.dependente ? props.item.dependente.id_texto : "" }}
            </td>
            <td class="text-xs-right">
              {{ props.item.total_valor | formatCurrency }}
            </td>
            <td align="center" style="min-width: 150px">
              <v-btn
                icon
                class="mx-0"
                @click="editItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'update')"
              >
                <v-icon color="teal">edit</v-icon>
              </v-btn>
              <v-btn
                icon
                class="mx-0"
                @click="deleteItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'delete')"
              >
                <v-icon color="pink">delete</v-icon>
              </v-btn>
            </td>
          </template>
          <template v-slot:footer>
            <td :colspan="temDependente ? 5 : 4">
              <strong>Total Geral</strong>
            </td>
            <td align="right">
              <strong>R$ {{ sum.total_valor | formatDouble }}</strong>
            </td>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ props.pageStart }} - {{ props.pageStop }} de
            {{ props.itemsLength }}
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!--Create Edit-->
    <v-dialog v-model="dialog" max-width="1600px" persistent>
      <v-card>
        <v-card-text>
          <v-container grid-list-md fluid>
            <v-layout wrap justify-right align-right>
              <v-flex xs12>
                <span class="display-1">{{ formTitle }}</span>
              </v-flex>
              <v-flex xs12 md8>
                <v-text-field
                  name="id_texto"
                  v-model="editedItem.id_texto"
                  counter
                  maxlength="60"
                  v-validate="'required'"
                  data-vv-validate-on="blur"
                  :error-messages="errors.collect('id_texto')"
                  :data-vv-as="translate('table.' + entity + '.field.id_texto')"
                  :label="translate('table.' + entity + '.field.id_texto')"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md4>
                <v-autocomplete
                  name="codigo"
                  v-model="editedItem.codigo"
                  data-vv-validate-on="blur"
                  v-validate="'required'"
                  :error-messages="errors.collect('codigo')"
                  :data-vv-as="translate('table.' + entity + '.field.codigo')"
                  :label="translate('table.' + entity + '.field.codigo')"
                  :items="translate('value.rend_tributacao_codigo')"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 md6>
                <v-text-field
                  name="sequencia_ir"
                  data-vv-validate-on="blur"
                  v-model="editedItem.sequencia_ir"
                  counter
                  maxlength="19"
                  v-validate="'required'"
                  @blur="checkDuplicate(items, 'sequencia_ir', editedItem.sequencia_ir)"
                  :error-messages="errors.collect('sequencia_ir')"
                  :data-vv-as="translate('table.' + entity + '.field.sequencia_ir')"
                  :label="translate('table.' + entity + '.field.sequencia_ir')"
                ></v-text-field>
                <div
                  style="color: #ff5252; font-size: 12px !important, position: relative; margin-top: -20px"
                  v-if="sequencia_ir_duplicated"
                >
                  Este valor já foi inserido anteriormente.
                </div>
              </v-flex>
              <v-flex xs12 md6>
                <p-cpf-cnpj
                  name="cnpj"
                  v-model="editedItem.cnpj"
                  v_validate="required"
                ></p-cpf-cnpj>
              </v-flex>
              <v-flex xs12 v-if="temDependente">
                <v-autocomplete
                  :label="translate('table.' + entity + '.field.pessoa_dependente_id')"
                  v-model="editedItem.pessoa_dependente_id"
                  :items="dependente_items"
                  item-text="id_texto"
                  item-value="id"
                ></v-autocomplete>
              </v-flex>

              <!--INICIO------------------- Distribuição ------------------->
              <v-flex xs12 md8>
                <span class="headline">Distribuições</span>
              </v-flex>
              <v-flex xs12 md4 class="text-xs-right">
                <v-btn color="primary" @click="createSubItem()" class="my-1">
                  {{ this.translate("system.button.add.title") }}
                  <v-icon dark>
                    {{ this.translate("system.button.add.icon") }}
                  </v-icon>
                </v-btn>
              </v-flex>
              <v-flex xs12>
                <v-data-table
                  :headers="sub_headers"
                  :items="editedItem.sub_items"
                  id="sub_items_table"
                  hide-actions
                  no-data-text="Não existem registros"
                  no-results-text="Nenhum registro encontrado"
                >
                  <template slot="items" slot-scope="props">
                    <td>
                      <p-date limitPreviousYears v-model="props.item.data"></p-date>
                    </td>
                    <td>
                      <p-money
                        name="valor"
                        v-model="props.item.valor"
                        :v_validate="'positiveNumber'"
                      ></p-money>
                    </td>
                    <td align="center">
                      <v-btn icon class="mx-0" @click="deleteSubItem(props.item)">
                        <v-icon color="pink">delete</v-icon>
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>
              </v-flex>
              <v-flex xs12 md3 class="text-xs-right">
                <p-money
                  name="valor"
                  :label="translate('table.' + entity + '.field.valor')"
                  v-model="editedItemSum.valor"
                  :v_validate="'positiveNumber'"
                ></p-money>
              </v-flex>
              <!--FIM------------------- Distribuição ------------------->

              <v-flex xs12>
                <v-btn class="mx-1" color="primary darken-2" :disabled="saving" :loading="saving" @click.native="save()">Salvar</v-btn>
                <v-btn class="mx-1" @click.native="close(400)">Cancelar</v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import crud from "./../../mixin/crud";
import PDate from "../shared/PDate";
import PMoney from "../shared/PMoney";
import Helper from "./../../classes/helper";
import acl from "./../../mixin/acl";
import PCpfCnpj from "../shared/PCpfCnpj";

export default {
  components: {
    PDate,
    PMoney,
    PCpfCnpj,
  },
  mixins: [crud, acl],
  data: () => ({
    entity: "condominio_rendimentotributacao",
    items: [],
    sum: {
      total_valor: 0,
    },
    sequencia_ir_duplicated: false,
  }),
  name: "RendimentoTributacao",
  props: ["condominio_id", "values", "initials", "temDependente", "dependente_items"],
  created() {
    this.items = this.values;
    this.editedItem = {
      condominio_id: this.condominio_id,
      ativo: true,
      sub_items: [],
      pessoa_dependente_id: null,
    };
    this.defaultItem = {
      condominio_id: this.condominio_id,
      ativo: true,
      sub_items: [],
      pessoa_dependente_id: null,
    };
  },
  watch: {
    items(newValue, oldValue) {
      this.updateSum();
    },
  },
  computed: {
    headers: function () {
      if (this.temDependente) {
        return [
          {
            text: this.translate("table." + this.entity + ".field.sequencia_ir"),
            value: "sequencia_ir",
            width: "10%",
          },
          {
            text: this.translate("table." + this.entity + ".field.id_texto"),
            value: "id_texto",
            width: "30%",
          },
          {
            text: this.translate("table." + this.entity + ".field.codigo"),
            value: "codigo",
            width: "5%",
          },
          {
            text: this.translate("table." + this.entity + ".field.cnpj"),
            value: "cnpj",
            width: "15%",
          },
          {
            text: this.translate("table." + this.entity + ".field.pessoa_dependente_id"),
            value: "dependente.id_texto",
            width: "20%",
          },
          {
            text: this.translate("table." + this.entity + ".field.valor"),
            value: "valor",
            width: "10%",
            align: "right",
          },
          {
            text: this.translate("system.table.action"),
            value: "action",
            sortable: false,
            align: "center",
            width: "10%",
          },
        ];
      } else {
        return [
          {
            text: this.translate("table." + this.entity + ".field.sequencia_ir"),
            value: "sequencia_ir",
            width: "10%",
          },
          {
            text: this.translate("table." + this.entity + ".field.id_texto"),
            value: "id_texto",
            width: "50%",
          },
          {
            text: this.translate("table." + this.entity + ".field.codigo"),
            value: "codigo",
            width: "5%",
          },
          {
            text: this.translate("table." + this.entity + ".field.cnpj"),
            value: "cnpj",
            width: "15%",
          },
          {
            text: this.translate("table." + this.entity + ".field.valor"),
            value: "valor",
            width: "10%",
            align: "right",
          },
          {
            text: this.translate("system.table.action"),
            value: "action",
            sortable: false,
            align: "center",
            width: "10%",
          },
        ];
      }
    },
    sub_headers: function () {
      return [
        {
          text: this.translate("table." + this.entity + "_item.field.data"),
          sortable: false,
          value: "data",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.valor"),
          sortable: false,
          value: "valor",
        },
        {
          text: this.translate("system.table.action"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    editedItemSum() {
      return this.editedItem.sub_items.reduce(
        (acc, cur) => {
          acc["valor"] += cur["valor"];
          return acc;
        },
        {
          valor: 0,
        }
      );
    },
  },
  methods: {
    checkDuplicate(items, index, value) {
      // this.sequencia_ir_duplicated = items.find((item) => {
      //   return item[index] === value;
      // });
      // if (this.sequencia_ir_duplicated) {
      //   event.preventDefault();
      //   event.target.select();
      // }
    },
    beforeCreate() {
      this.editedItem.sequencia_ir = Helper.generateSequenciaIr(this.initials, "RT", this.values);
    },
    createSubItem: function () {
      this.editedItem.sub_items.push({
        tipo: "pagamento",
        data: "",
        valor: 0,
      });
    },
    deleteSubItem(item) {
      const index = this.editedItem.sub_items.indexOf(item);
      this.editedItem.sub_items.splice(index, 1);
    },
    processSituacao() {
      this.editedItem.valor = this.editedItem.sub_items.reduce((acumulador, valorAtual) => {
        return acumulador + valorAtual.valor;
      }, 0);
    },
    updateSum() {
      this.sum = this.$refs.RendimentoTributacaoTable.filteredItems.reduce(
        (acc, item) => {
          const total = item.sub_items.reduce(
            (acc2, sub_item) => {
              acc2.total_valor += sub_item.valor;
              return acc2;
            },
            {
              total_valor: 0,
            }
          );
          acc.total_valor += total.total_valor;
          item.total_valor = total.total_valor;

          return acc;
        },
        {
          total_valor: 0,
        }
      );

      console.log(this.sum);
    },
    getCodigoText (codigo) {
      const translate = 'value.rend_tributacao_codigo'
      const item = this.translate(translate).find(c => c.value === codigo)
      return item ? item.text : ''
    },
    // type 1 = pdf 2 = excel
    exportReport (type) {
      this.$store.commit("loader/change_dialog", true);
      this.$http.put(`report/report_details/${this.condominio_id}`, {"type": type, "table": "condominio_rendimentotributacao", "search": this.search, "apenasAtivos": this.apenasAtivos}).then(
        (res) => {
          let fileName = res.data;
          window.open(process.env.API + "reports/" + fileName, "_blank");
          this.$store.commit("loader/change_dialog", false);
          setTimeout(function () {
            _this.$http.delete(`report/delete_upload_report/${fileName}`)
          }, 5000)
        },
        (error) => {
          this.$store.commit("loader/change_dialog", false);
          this.$store.commit("snackbar/change_values", {
            text:
              error.body.message ||
              "Desculpe, não conseguimos gerar o relatório no momento. Por favor, tente novamente mais tarde.",
            color: "error",
            dialog: true,
          });
        }
      );
    }
  },
};
</script>

<style scoped>
#sub_items_table {
  border-radius: 4px;
  border: 1px solid #bbb;
  margin-bottom: 4px;
}

#sub_items_table .custom-height {
  padding-top: 8px !important;
}

td .custom-height {
  padding-top: 6px;
  display: flex;
  align-items: center;
}

#proccess {
  position: absolute;
}

#proccess .v-icon {
  position: relative;
  bottom: 50px;
  left: 233px;
}
</style>


// WEBPACK FOOTER //
// src/components/details/RendimentoTributacao.vue